import { WhatsappChat } from '@factorialco/f0-react/icons/app'
import { AvailableModules } from 'generated/featureDefinitions'
import { EmployeeRouteMetaDocument, EmployeeSelectorDocument } from 'generated/resources/graphql'

import { getWhatsappUrl } from 'modules/core/components/WhatsAppButton'
import { createGraphqlDataSourceDefinition } from 'modules/core/hooks/useGraphqlDataSource'
import buildUrl from 'modules/core/lib/buildUrl'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import { createBreadcrumbSelectDefinition } from 'modules/core/lib/navigation'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import { MetaContext, MetaValue } from 'modules/core/lib/navigation/routes'
import { ResourceMeta } from 'modules/core/lib/navigation/types'

export const buildEmployeeMeta = (
  { watchQuery, params, session }: MetaContext,
  navigation: Navigation<NavigationPaths>
): MetaValue<ResourceMeta> => {
  const { id } = params
  const companyId = session.company.id

  if (!id) {
    return {
      title: '',
    }
  }

  return watchQuery({
    query: EmployeeRouteMetaDocument,
    variables: {
      id: Number(id),
      companyId,
      moduleAvailabilityIds: [
        AvailableModules.timeoff,
        AvailableModules.competencies,
        AvailableModules.trainings,
      ],
    },
  }).map(({ data }) => {
    const employee = data.employees.employee
    if (!employee) {
      return { title: '' }
    }

    const activeFeatures = new Set(
      (data?.features?.features ?? [])
        .filter((feature) => feature.enabled)
        .map((activeFeature) => activeFeature.name)
    )

    const isManagedByMe = data?.employees.managed.nodes.some((e) => e.id === Number(employee.id))
    const isMyProfile = session.employee?.id === Number(employee.id)
    const canSeeContract = employee._policies?.seeContract?.can
    const canEditContract = employee._policies?.editContract?.can
    const employeeHasContract = Boolean(employee.referenceContract)
    const canSeeContractsTab = canSeeContract && (canEditContract || employeeHasContract)
    const seeTalentTabOption = activeFeatures.has('dev_performance_employee_talent_tab')
    const canSeePerformance =
      (employee._policies?.seeEmployeePerformanceTab?.can && !seeTalentTabOption) ?? false
    const canSeeTalent =
      (employee._policies?.seeEmployeeTalentPage?.can && seeTalentTabOption) ?? false

    const canRequestUpsell = data?.upsell._policies.canRequestUpsell?.can

    const hasTrainings = isModuleAvailabilityEnabled(
      data.companies.moduleAvailabilities,
      AvailableModules.trainings
    )

    const timeoffEnabled = isModuleAvailabilityEnabled(
      data.companies.moduleAvailabilities,
      AvailableModules.timeoff
    )
    const canSeeTimeOffTab = timeoffEnabled && employee._policies?.readLeaves?.can

    const canSeeTimePlanningTab = employee._policies?.seePlanningVersions?.can ?? false

    const canSeeAttendance = employee._policies?.seeAttendance?.can ?? false

    const canSeeAssessments = employee._policies?.seeEmployeeCompetenciesAssessments?.can ?? false

    const canBecome = employee.access._policies?.become.can ?? false

    const canDisableMfa = employee.authenticationAccess?._policies?.disableMfa.can ?? false

    const canInvite = employee._policies?.inviteEmployeeToApp.can ?? false

    const canTerminate = employee._policies?.terminateEmployees.can ?? false

    const canSeeAnyEmployeeDocumentsByFolder =
      employee._policies?.seeAnyEmployeeDocumentsByFolder?.can ?? false
    const canSeeEmployeeDocumentsWithoutFolder =
      employee._policies?.seeEmployeeDocumentsWithoutFolder?.can ?? false

    const previousEmployee = data.employees.previousEmployee?.nodes[0]
    const nextEmployee = data.employees.nextEmployee?.nodes[0]
    const canManageAnyEmployeeFolder =
      data.documents?._policies?.employeeManageAnyEmployeeFolder?.can

    const canSeeTasksTab = employee._policies?.manageTasks?.can
    const canSeeTrainingsTab = employee._policies?.seeEmployeeTrainings?.can

    const canSeeDocumentsTab =
      (employee._policies?.seeDocuments?.can ||
        canManageAnyEmployeeFolder ||
        canSeeAnyEmployeeDocumentsByFolder ||
        canSeeEmployeeDocumentsWithoutFolder) ??
      false

    const overviewEnabledByFeatures =
      [
        canSeeContractsTab,
        canSeeTimeOffTab,
        canSeeTasksTab,
        canSeeTimePlanningTab,
        canSeeAttendance,
      ].filter(Boolean).length >= 2

    // this condition is going to be removed soon in Q2 (2025) because we will
    // always show the overview when we have content ready for everyone to see
    const canSeeOverview = isManagedByMe || isMyProfile || overviewEnabledByFeatures

    const employeeName = employee.preferredName || employee.firstName
    const employeeDisplayFullName = getEmployeeDisplayName(employee)
    const [hasWhatsappUrl] = getWhatsappUrl({
      phoneNumber: employee.phoneNumber ?? undefined,
      country: employee.country ?? undefined,
    })
    const isEmployeeTerminated = !!employee.terminatedOn
    const isEmployeeInvited = !!employee.access.invitedOn
    const isEmployeeJoined = employee.access.isConfirmed ?? false
    const isMfaEnabled = employee.authenticationAccess?.mfaEnabled ?? false
    const hasRegularAccess = employee.hasRegularAccess ?? true

    const source = createGraphqlDataSourceDefinition({
      document: EmployeeSelectorDocument,
      mapVariables: ({ search }) => {
        return {
          companyId,
          active: true,
          onlyManagers: false,
          search: search ?? '',
          excludeIds: [],
        }
      },
      mapData: (queryData) => {
        return queryData.employees.employeeNamesConnection.edges.map((edge) => edge.node)
      },
      mapPagination: (queryData) => {
        const connection = queryData.employees.employeeNamesConnection
        const pageInfo = connection.pageInfo
        return {
          total: 0, // TODO: get total from the query or remove the todo need
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage: pageInfo.hasNextPage ?? false,
        }
      },
      paginationType: 'infinite-scroll',
    })

    const breadcrumbSelect = createBreadcrumbSelectDefinition<
      ReturnType<typeof source.mapData>[number],
      // Todo: fix this type
      typeof source
    >({
      url: (value: string) => navigation.employees.id.fullPath({ id: value }),
      mapOptions: (employeeItem) => {
        return {
          label: getEmployeeDisplayName(employeeItem),
          value: employeeItem.id.toString(),
          avatar: {
            type: 'person' as const,
            firstName: employeeItem.preferredName || employeeItem.firstName,
            lastName: employeeItem.lastName,
            src: buildUrl(employeeItem.access?.avatar) || undefined,
          },
        }
      },
      source,
      defaultItem: {
        label: getEmployeeDisplayName(employee),
        value: employee.id.toString(),
        avatar: {
          type: 'person' as const,
          firstName: employee.preferredName || employee.firstName,
          lastName: employee.lastName,
          src: buildUrl(employee.access?.avatar) || undefined,
        },
      },
    })

    return {
      title: employeeDisplayFullName,
      headerComponentProps: {
        title: employeeDisplayFullName,
        avatar: {
          src: buildUrl(employee.avatar?.url) || undefined,
          firstName: employeeName,
          lastName: employee.lastName,
          type: 'person',
        },
        description: employee.referenceContract?.jobTitle ?? '',
        secondaryActions: [
          {
            key: 'whatsapp-chat',
            label: i18n.t('common.chat_on_whatsapp.call_to_action'),
            icon: WhatsappChat,
            disabled: !hasWhatsappUrl,
            tooltip: !hasWhatsappUrl
              ? i18n.t('common.chat_on_whatsapp.phone_not_available', {
                  employee_name: employee.fullName,
                })
              : undefined,
            isVisible:
              employee._policies?.readPersonalInfo.can &&
              activeFeatures.has('employee_chat_on_whatsapp'),
          },
        ],
        otherActions: isMyProfile
          ? []
          : [
              {
                key: 'revoke-invite',
                label: i18n.t('employees.uninvite.action'),
                isVisible:
                  canInvite && !isEmployeeTerminated && isEmployeeInvited && !isEmployeeJoined,
              },
              {
                key: 'unterminate',
                label: i18n.t('employees.untermination.action'),
                isVisible: canTerminate && isEmployeeTerminated && !employee.isTerminating,
              },
              {
                key: 'become',
                label: i18n.t('employees.become.action', {
                  name: employeeName,
                }),
                isVisible: canBecome,
              },
              {
                key: 'move-to-welcome-space',
                label: i18n.t('employees.welcome_space.action'),
                isVisible:
                  canInvite &&
                  !isEmployeeJoined &&
                  hasRegularAccess &&
                  activeFeatures.has('employee_onboarding'),
              },
              {
                key: 'disable-mfa',
                label: i18n.t('employees.disable_mfa.action', {
                  name: employeeName,
                }),
                isVisible: canDisableMfa && isMfaEnabled,
              },
              {
                key: 'terminate',
                label: i18n.t('employees.termination.action', {
                  name: employeeName,
                }),
                critical: true,
                isVisible: canTerminate && !isEmployeeTerminated && !employee.isTerminating,
              },
            ],
      },
      siblingsNavigation: {
        previous: previousEmployee
          ? {
              title: getEmployeeDisplayName(previousEmployee),
              url: navigation.employees.id.fullPath({ id: previousEmployee.id }),
            }
          : undefined,
        next: nextEmployee
          ? {
              title: getEmployeeDisplayName(nextEmployee),
              url: navigation.employees.id.fullPath({ id: nextEmployee.id }),
            }
          : undefined,
        select: breadcrumbSelect,
      },
      subNavigation: [
        {
          title: i18n.t('overview.title'),
          href: canSeeOverview
            ? navigation.employees.id.fullPath({ id: employee.id })
            : navigation.employees.id.overview.fullPath({ id: employee.id }),
          index: canSeeOverview,
          isVisible: canSeeOverview,
        },
        {
          title: i18n.t('profile.work_details'),
          href: !canSeeOverview
            ? navigation.employees.id.fullPath({ id: employee.id })
            : navigation.employees.id.workDetails.fullPath({ id: employee.id }),
          isVisible: data.apiCore._policies.companySeeEmployeeProfile.can,
          index: !canSeeOverview,
        },
        {
          title: i18n.t('profile.personal_details'),
          href: navigation.employees.id.personal.fullPath({ id: employee.id }),
          isVisible: employee._policies?.readPersonalInfo?.can,
        },
        {
          title: i18n.t('admin_employee.contracts'),
          href: navigation.employees.id.contractVersions.fullPath({ id: employee.id }),
          isVisible: canSeeContractsTab,
        },
        {
          title: i18n.t('admin_employee.documents'),
          href: navigation.employees.id.documents.list.fullPath({ id: employee.id }),
          isVisible: canSeeDocumentsTab,
        },
        {
          title: i18n.t('time_planning.title'),
          href: navigation.employees.id.planningVersions.fullPath({ id: employee.id }),
          isVisible: canSeeTimePlanningTab,
        },
        {
          title: i18n.t('time_off.title'),
          href: navigation.employees.id.timeOff.fullPath({ id: employee.id }),
          isVisible: canSeeTimeOffTab,
        },
        {
          title: i18n.t('sidebar.performance_new'),
          href: navigation.employees.id.performance.fullPath({ id }),
          isVisible: canSeePerformance,
        },
        {
          title: i18n.t('sidebar.talent'),
          href: navigation.employees.id.talent.fullPath({ id }),
          isVisible: canSeeTalent,
        },
        {
          title: i18n.t('tasks.header'),
          href: navigation.employees.id.tasks.fullPath({ id: employee.id }),
          isVisible: canSeeTasksTab,
        },
        {
          title: i18n.t('competencies_v2.title'),
          href: navigation.employees.id.competencies.fullPath({ id: employee.id }),
          isVisible: canSeeAssessments,
        },
        {
          title: i18n.t('trainings.my_training.all_courses_table.training'),
          href: navigation.employees.id.training.fullPath({ id: employee.id }),
          isVisible: canSeeTrainingsTab,
          variant: !hasTrainings && canRequestUpsell ? 'upsell' : 'default',
        },
        {
          title: i18n.t('custom_tables.header'),
          href: navigation.employees.id.customTables.fullPath({ id: employee.id }),
          isVisible: true,
        },
      ],
    }
  })
}
